/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IPriceComponentResources } from '@msdyn365-commerce/components';
import MsDyn365 from '@msdyn365-commerce/core';
import { RichTextComponent } from '@msdyn365-commerce/core';
import { AsyncResult, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    ICategoryHierarchyViewProps,
    IRefineMenuViewProps,
    ISearchResultContainerViewProps,
    ISearchResultModalViewProps,
    ISortByViewProps,
    ITitleViewProps
} from '@msdyn365-commerce-modules/search-result-container';
import { Button, getTelemetryObject, Module, Node } from '@msdyn365-commerce-modules/utilities';
import React, { MouseEventHandler, useEffect, useState, useLayoutEffect } from 'react';

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import {
    IExtSearchResultContainerProps,
    IExtSearchResultContainerResources
} from '../definition-extensions/ext-search-result-container.ext.props.autogenerated';
import { ProductComponent } from './components/product.component';
import { ICategoryExt } from '../../../custom-proxy/DataServiceEntities.g';
import classname from 'classnames';
import { useCategoryViewTracker } from '../../../integrations/relewise/hooks/useCategoryViewTracker';

/**
 * DescriptionInterval.
 */
enum DescriptionInterval {
    productDescriptionInterval = 14,
    productDescriptionIntervalRemainder0 = 0,
    productDescriptionIntervalRemainder9 = 9
}

export interface IExtSearchResultContainerData {
    categorySeo: AsyncResult<ICategoryExt>;
}

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const renderProductCards = (props: ISearchResultContainerViewProps & IExtSearchResultContainerProps<{}>): JSX.Element | null => {
    return <Node {...props.ProductsContainer}>{props.products}</Node>;
};

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @param product - The product item data.
 * @param index - The index of each product item.
 * @param isEnabledProductDescription - The flag for showing Product desription tile.
 * @returns The JSX Element.
 */
const renderProductCardsDescription = (
    props: ISearchResultContainerViewProps & IExtSearchResultContainerProps<{}>,
    product: ProductSearchResult,
    index: number,
    isEnabledProductDescription?: boolean
): JSX.Element | null => {
    const { config, context, resources, productsDimensionAvailabilities } = props;
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const breadCrumbType = props.context.app.config.breadcrumbType;
    const isAllowBack = props.config.allowBackNavigation && (breadCrumbType === 'back' || breadCrumbType === 'categoryAndBack');
    const classname = isEnabledProductDescription
        ? 'ms-product-search-result__item product__description'
        : 'ms-product-search-result__item';
    const quickviewslot = ArrayExtensions.hasElements(props.slots.quickview) ? props.slots.quickview[0] : undefined;
    const channelInventoryConfigurationId = props.data.products.result?.channelInventoryConfigurationId;
    const isPriceMinMaxFeatureState = props.data.featureState.result?.find(
        featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
    );
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };

    const productComparisonButton = ArrayExtensions.hasElements(props.slots.productComparisonButton)
        ? props.slots.productComparisonButton[0]
        : null;

    return (
        <li className={classname} key={index}>
            <ProductComponent
                context={context}
                telemetryContent={telemetryContent}
                imageSettings={config.imageSettings}
                freePriceText={resources.priceFree}
                originalPriceText={resources.originalPriceText}
                currentPriceText={resources.currentPriceText}
                ratingAriaLabel={resources.ratingAriaLabel}
                allowBack={isAllowBack}
                id={props.id}
                key={product.RecordId}
                typeName={props.typeName}
                data={{ product }}
                quickViewButton={quickviewslot}
                productComparisonButton={productComparisonButton}
                isEnabledProductDescription={isEnabledProductDescription}
                inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                priceResources={priceResources}
                swatchItemAriaLabel={resources.swatchItemAriaLabel}
                dimensionAvailabilities={productsDimensionAvailabilities?.find(
                    (dimensionAvailability: IProductsDimensionsAvailabilities[]) =>
                        ArrayExtensions.hasElements(dimensionAvailability) &&
                        dimensionAvailability[0].masterProductId === product.MasterProductId
                )}
            />
        </li>
    );
};

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const renderProducts = (props: ISearchResultContainerViewProps & IExtSearchResultContainerProps<{}>): JSX.Element | null => {
    const products = props.data.listPageState.result?.activeProducts;
    if (!products || !ArrayExtensions.hasElements(products)) {
        return null;
    }
    const productDescriptionInterval: number = DescriptionInterval.productDescriptionInterval;
    const productDescriptionIntervalRemainder0: number = DescriptionInterval.productDescriptionIntervalRemainder0;
    const productDescriptionIntervalRemainder9: number = DescriptionInterval.productDescriptionIntervalRemainder9;
    return (
        <Node {...props.ProductsContainer}>
            {props.errorMessage}
            <ul className='list-unstyled'>
                {products.map((product: ProductSearchResult, index: number) =>
                    index % productDescriptionInterval === productDescriptionIntervalRemainder0 ||
                    index % productDescriptionInterval === productDescriptionIntervalRemainder9
                        ? renderProductCardsDescription(props, product, index, true)
                        : renderProductCardsDescription(props, product, index, false)
                )}
            </ul>
        </Node>
    );
};

/**
 * Render Title Count.
 * @param props - The title view props.
 * @returns The JSX Element.
 */
const renderTitleCount = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return <Node {...TitleContainer}>{title.titleCount}</Node>;
    }
    return null;
};

/**
 * Render Category Hierarchy.
 * @param props - The category hierarchy view props.
 * @returns The JSX Element.
 */
const renderCategoryHierarchy = (props: ICategoryHierarchyViewProps): JSX.Element | null => {
    const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = props;
    if (categoryHierarchyList) {
        return (
            <Node {...CategoryHierarchyContainer}>
                {categoryHierarchyList.map((category, index) => (
                    <React.Fragment key={index}>
                        {category}
                        {categoryHierarchyList[index + 1] && categoryHierarchySeparator}
                    </React.Fragment>
                ))}
            </Node>
        );
    }

    return null;
};

/**
 * Function to make filter sticky.
 * @param isMobile - The boolean prop.
 * @param isStickyHeader - The boolean prop.
 */
export const configureStickyFilter = (isMobile: boolean, isStickyHeader: boolean): void => {
    // Get heights of cookie and promotion banners
    const defaultValue = 0;
    const defaultOffset = 28;
    const titleContainer: HTMLElement | null = document.querySelector('.ms-search-result-container__category-nav-section');

    const style = titleContainer ? getComputedStyle(titleContainer) : undefined;
    const marginBottom = style ? parseFloat(style.marginBottom) : undefined;
    const titleHeight: number | undefined = (titleContainer?.offsetHeight ?? 0) + (marginBottom ?? 0);
    const containerHeight: number = titleHeight ?? defaultOffset;
    const bannerHeights = isStickyHeader ? defaultValue : containerHeight;
    const headerElement = document.querySelector('header .lock-opaque');
    const defaultContainer = document.querySelector('header .default-container');
    const refinerElement = isMobile ? document.querySelector('.msc-sort-filter-wrapper') : document.querySelector('.ms-refiner-heading');

    if (refinerElement && headerElement && defaultContainer) {
        if (MsDyn365.isBrowser && document.documentElement.scrollTop > bannerHeights) {
            refinerElement.classList.add('lock-Filter');
        } else {
            refinerElement.classList.remove('lock-Filter');
        }
    } else if (refinerElement) {
        if (MsDyn365.isBrowser && document.documentElement.scrollTop > bannerHeights) {
            refinerElement.classList.add('lock-Filter');
        } else {
            refinerElement.classList.remove('lock-Filter');
            refinerElement.removeAttribute('style');
        }
    }
};

/**
 * Function to setup filter sticky.
 * @param isMobile - The boolean prop.
 * @param isStickyHeader - The boolean prop.
 */
function stickyFilterSetup(isMobile: boolean, isStickyHeader: boolean): void {
    window.addEventListener('scroll', () => {
        configureStickyFilter(isMobile, isStickyHeader);
    });
    configureStickyFilter(isMobile, isStickyHeader);
}

/**
 * Function to make filter sticky.
 * @param refinerRefernce - Ref prop for filter.
 * @param toggle - Toggle function for filter menu.
 */
function useOutsideClick(refinerRefernce: React.RefObject<HTMLElement>, toggle: (event: Event) => void) {
    React.useEffect(() => {
        /**
         * Function to make filter sticky.
         * @param event - Event prop for filter.
         */
        function handleClickOutside(event: Event) {
            const element = document.querySelector('.ms-refine-filter__toggle_collapsed');
            if (refinerRefernce.current && !refinerRefernce.current.contains(event.target as Node) && !element) {
                toggle(event);
            }
        }
        if (MsDyn365.isBrowser) {
            document.addEventListener('click', handleClickOutside);
        }
        return () => {
            if (MsDyn365.isBrowser) {
                document.removeEventListener('click', handleClickOutside);
            }
        };
    }, [refinerRefernce, toggle]);
}

/**
 * Render the category page items.
 * @param props - The view props.
 * @returns The search Result container module view.
 */
const SearchResultContainerView: React.FC<ISearchResultContainerViewProps &
    IExtSearchResultContainerProps<IExtSearchResultContainerData>> = props => {
    const {
        SearchResultContainer,
        pagination,
        ProductSectionContainer,
        choiceSummary,
        isMobile,
        modalToggle,
        searchResultModal,
        TitleViewProps,
        refineMenu,
        categoryHierarchy,
        sortByOptions,
        CategoryNavContainer,
        RefineAndProductSectionContainer,
        FeatureSearchContainer,
        similarLookProduct,
        errorMessage,
        resources,
        slots
    } = props;
    useEffect(() => {
        if (props.config.useStickyFilter && MsDyn365.isBrowser) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app context is generic
            stickyFilterSetup(props.isMobile, props.context.app.config.enableStickyHeader as boolean);
        }
    }, [props]);

    useCategoryViewTracker(props.data.category.result?.NeutralizedName);

    const hideFilterElRef = React.useRef<HTMLDivElement>(null); //the element that upon intersection triggers the filter to hide
    const filterElRef = React.useRef<HTMLDivElement>(null);
    const filterHidingObserver = React.useRef<IntersectionObserver | null>(null);
    const wrapperRefiner = React.useRef<HTMLDivElement | null>(null);
    const [filterIsLocked, setFilterIsLocked] = useState<boolean>(false);

    useFilterLockObserver(isMobile ? filterElRef : wrapperRefiner, setFilterIsLocked);
    useFloatingFilter(isMobile ? filterElRef : wrapperRefiner, filterHidingObserver, hideFilterElRef, filterIsLocked);

    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const toggle = () => {
        setIsExpanded(!isExpanded);
    };
    useLockBodyScroll(isExpanded);

    useOutsideClick(wrapperRefiner, toggle);
    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;
    const showCategoryDescription = props.config.categoryDescription;
    if (isMobile) {
        return (
            <Module {...SearchResultContainer} ref={hideFilterElRef}>
                {similarLookProduct ? null : (
                    <Node {...CategoryNavContainer}>
                        {renderCategoryHierarchy(categoryHierarchy)}
                        {renderTitle(TitleViewProps)}
                        {renderTitleCount(TitleViewProps)}
                    </Node>
                )}
                {choiceSummary}
                {slots.subCategoryLinks}
                <div className='bs-refiner-heading-container'>
                    <Node className='msc-sort-filter-wrapper' ref={filterElRef}>
                        {modalToggle}
                    </Node>
                </div>
                {createSearchResultModal(searchResultModal, refineMenu, sortByOptions, isRecoSearchPage)}
                <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                {errorMessage}
                {props.config.enableProdutDescription ? renderProducts(props) : renderProductCards(props)}
                <Node className='paginationWrapper'>{pagination}</Node>
                {showCategoryDescription ? (
                    <Node className='ms-search-result-container__category-description'>{renderCategoryDescription(props)}</Node>
                ) : null}
                {renderSeoDescription(props)}
            </Module>
        );
    }

    return (
        <Module {...SearchResultContainer}>
            {similarLookProduct ? null : (
                <Node {...CategoryNavContainer}>
                    {categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}
                    {TitleViewProps && renderTitle(TitleViewProps)}
                    {TitleViewProps && renderTitleCount(TitleViewProps)}
                </Node>
            )}
            <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
            <Node {...RefineAndProductSectionContainer}>
                {slots.subCategoryLinks}
                <div className='bs-refiner-heading-container'>
                    <Node ref={wrapperRefiner} className='ms-refiner-heading'>
                        <Node className='ms-refiner-heading_title'>
                            <Button
                                className={isExpanded ? 'ms-refine-filter__toggle_expanded' : 'ms-refine-filter__toggle_collapsed'}
                                aria-label='Filter'
                                onClick={toggle}
                                aria-expanded={isExpanded}
                                tabIndex={0}
                            >
                                {filterIsLocked && !isExpanded ? resources.modalTitle : resources.filterText}
                            </Button>
                            <div className='ms-search-result-wrapper-title-choice-summary'>
                                {!filterIsLocked || isExpanded ? choiceSummary : null}
                            </div>
                            <div className='ms-search-result-wrapper-sort-by-category'>
                                {!filterIsLocked || isExpanded ? sortByOptions && !isRecoSearchPage && renderSort(sortByOptions) : null}
                            </div>
                        </Node>
                        {refineMenu && isExpanded && (
                            <div className='ms-refine__floating-filters'>
                                {renderRefiner(refineMenu)}
                                <Node className='ms-refine__footer'>{renderRefinerFooter(refineMenu, toggle, resources)}</Node>
                            </div>
                        )}
                    </Node>
                </div>
                <Node {...ProductSectionContainer} ref={hideFilterElRef}>
                    {refineMenu && (
                        <div
                            className={classname('filter-overlay', {
                                visible: isExpanded
                            })}
                        ></div>
                    )}
                    {errorMessage}
                    {props.config.enableProdutDescription ? renderProducts(props) : renderProductCards(props)}
                    <Node className='paginationWrapper'>{pagination}</Node>
                </Node>
                {showCategoryDescription ? (
                    <Node className='ms-search-result-container__category-description'>{renderCategoryDescription(props)}</Node>
                ) : null}
            </Node>
            {renderSeoDescription(props)}
        </Module>
    );
};

/**
 * Render Search result Modal.
 * @param modalProps - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createSearchResultModal = (
    modalProps: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element => {
    return React.cloneElement(
        modalProps.modal,
        {},
        modalProps.modalHeader,
        createModalBody(modalProps, refineMenu, sortByDropDown, isRecoSearchPage),
        modalProps.modalFooter
    );
};

//BS CUSTOM
const useFloatingFilter = (
    filterElRef: React.RefObject<HTMLElement>,
    filterHidingObserver: React.MutableRefObject<IntersectionObserver | null>,
    hideFilterElRef: React.RefObject<HTMLDivElement>,
    filterIsLocked: boolean
): void => {
    useEffect(() => {
        const options = {
            threshold: 0,
            rootMargin: '-80% 0px 0px 0px'
        };

        filterHidingObserver.current = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                filterElRef.current?.classList.toggle('slide-out', !entry.isIntersecting);
            });
        }, options);

        if (hideFilterElRef.current) {
            filterHidingObserver.current.observe(hideFilterElRef.current);
        }

        return () => {
            if (filterHidingObserver.current && hideFilterElRef.current) {
                filterHidingObserver.current.disconnect();
            }
        };
    }, [hideFilterElRef, filterElRef, filterIsLocked]);
};

//BS CUSTOM
const useFilterLockObserver = (targetRef: React.RefObject<HTMLElement>, setFilterIsLocked: (locked: boolean) => void) => {
    useEffect(() => {
        const observer = new MutationObserver(() => {
            if (targetRef.current) {
                setFilterIsLocked(targetRef.current?.classList.contains('lock-Filter') ?? false);
            }
        });

        const target = targetRef.current;
        if (target) {
            observer.observe(target, { attributes: true, attributeFilter: ['class'] });
            setFilterIsLocked((target as HTMLElement).classList.contains('lock-Filter'));
        }

        return () => observer.disconnect();
    }, []);
};

export function useLockBodyScroll(filterOpen: boolean) {
    useLayoutEffect(() => {
        const { body, documentElement: doc } = document;
        if (!filterOpen) return;

        const scrollBarWidth = window.innerWidth - doc.clientWidth;
        const prevOverflow = body.style.overflow;
        const prevPaddingRight = body.style.paddingRight;

        body.style.overflow = 'hidden';
        if (scrollBarWidth > 0) body.style.paddingRight = `${scrollBarWidth}px`;

        return () => {
            body.style.overflow = prevOverflow;
            body.style.paddingRight = prevPaddingRight;
        };
    }, [filterOpen]);
}

/**
 * Render Refiner Mobile.
 * @param props - The Refine Menu view props.
 * @returns The JSX Element.
 */
const renderRefinerMobile = (props: IRefineMenuViewProps): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {refiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render Search result Body.
 * @param props - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createModalBody = (
    props: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string
): JSX.Element | null => {
    if (sortByDropDown) {
        return React.cloneElement(props.modalBody, {}, renderSort(sortByDropDown, isRecoSearchPage), renderRefinerMobile(refineMenu));
    }
    return null;
};

/**
 * Render Refiner.
 * @param props - The Refine Menu view props.
 * @returns The JSX Element.
 */
const renderRefiner = (props: IRefineMenuViewProps): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {refiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render Refiner Footer.
 * @param props - The Refine Menu view props.
 * @param handler - The Refine menu toggle function.
 * @param resources - Resources to get the done text and aria label.
 * @returns The JSX Element.
 */
const renderRefinerFooter = (
    props: IRefineMenuViewProps,
    handler: MouseEventHandler,
    resources: IExtSearchResultContainerResources
): JSX.Element | null => {
    const { refiners } = props;
    if (refiners) {
        return (
            <Node className='ms-refine__footer__done'>
                <Node tag='button' className='ms-refine-filter-done' aria-label={resources.doneAriaLabel} tabIndex={0} onClick={handler}>
                    {resources.doneText}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render sort by drop down.
 * @param props - The sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
    const { SortingContainer, sortByDropDown } = props;
    if (sortByDropDown && !isRecoSearchPage) {
        return <Node {...SortingContainer}>{sortByDropDown}</Node>;
    }
    return null;
};

/**
 * Render Title.
 * @param props - The title view props.
 * @returns The JSX Element.
 */
const renderTitle = (props: ITitleViewProps): JSX.Element | null => {
    const { title, TitleContainer } = props;
    if (title) {
        return (
            <Node {...TitleContainer}>
                <Node tag='h1' className=''>
                    {title.titlePrefix}
                    {title.titleText}
                </Node>
            </Node>
        );
    }
    return null;
};
/**
 * Render Category description.
 * @param props - The props.
 * @returns The JSX Element.
 */
const renderCategoryDescription = (props: ISearchResultContainerViewProps): JSX.Element | null => {
    const description = props.data.category.result?.LocalizedDescription;
    if (description) {
        return <span>{description}</span>;
    }
    return null;
};

/**
 * Render Category SEO description.
 * @param props - The props.
 * @returns The JSX Element.
 */
const renderSeoDescription = (
    props: ISearchResultContainerViewProps & IExtSearchResultContainerProps<IExtSearchResultContainerData>
): JSX.Element | null => {
    const seoDescription = props?.data?.categorySeo?.result?.EcommerceSeoDescription;
    if (seoDescription?.length) {
        return (
            <div className='container default-container'>
                <div className='ms-text-block'>
                    <div>
                        <RichTextComponent text={seoDescription} />
                    </div>
                </div>
            </div>
        );
    }
    return null;
};

export default SearchResultContainerView;
